import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '../environments/environment';
import { Observable, BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SocketService {
  private socket!: Socket;
  private isConnected$ = new BehaviorSubject<boolean>(false);

  private getBaseSocketUrl(): string {
    // Remove trailing /api or /api/ from the URL
    return environment.apiUrl.replace(/\/api\/?$/, '');
  }

  connect(): void {
    if (!this.socket || !this.socket.connected) {
      const baseUrl = this.getBaseSocketUrl();

      this.socket = io(baseUrl, {
        transports: ['websocket'],
        withCredentials: false,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000
      });

      this.socket.on('connect', () => {
        console.log('✅ Socket connected:', this.socket.id);
        this.isConnected$.next(true);
      });

      this.socket.on('disconnect', (reason) => {
        console.log('⚠️ Socket disconnected:', reason);
        this.isConnected$.next(false);
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error);
        this.isConnected$.next(false);
      });
    }
  }

  getConnectionStatus(): Observable<boolean> {
    return this.isConnected$.asObservable();
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  emit(event: string, data?: any): void {
    if (this.socket?.connected) {
      console.log(`📤 Emitting event "${event}"`, data);
      this.socket.emit(event, data);
    } else {
      console.warn(`⚠️ Socket not connected. Cannot emit: "${event}"`);
    }
  }

  on<T>(event: string): Observable<T> {
    return new Observable<T>((observer) => {
      if (!this.socket) {
        console.warn(`⚠️ Cannot subscribe to "${event}" — socket not connected.`);
        return;
      }
      this.socket.on(event, (data: T) => {
        console.log(`📡 Received "${event}":`, data);
        observer.next(data);
      });
    });
  }

  joinCommunity(communityId: number): void {
    console.log('🔗 Joining community room:', `community-${communityId}`);
    this.emit('joinCommunity', communityId);
  }

  listenToNewPost(): Observable<any> {
    return this.on('new_post');
  }

  listenToPostUpdated(): Observable<any> {
    return this.on('post_updated');
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.isConnected$.next(false);
      console.log('🔌 Socket disconnected manually');
    }
  }
}
